import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import { UpdatedNode, NewNode } from '../../interfaces/nodeDefinitions';
import { toast } from 'react-toastify';
import { flattenQuery } from 'helpers/flattenQuery';
import { extractErrorMessage } from 'helpers/extractErrorMessage';
import { FilterConfig } from 'hooks/useListQuery';
import type { RetentionType } from 'api/shared/retentionPolicy';

export interface RetentionPolicyRule {
  status: string; // 'all' | 'completed' | 'failed' | ...
  retention_type: RetentionType;
  value: number; // >= 0
}

// Define the structure of customer node definitions
export interface CustomerNodeDefinition {
  restrict_to_scheduling_window: number | null;
  node_definition_id: number;
  type: string;
  name: string;
  retention_policies?: RetentionPolicyRule[];
  status?: string;
  references?: number;
  version?: number; // <-- added to hold concurrency version
  auto_restart_enabled?: boolean;
  auto_restart_amount?: number;
  auto_restart_delay_seconds?: number;
  hold_on_submit?: boolean;

  runtime_limit_min_hhmmss?: string | null;
  runtime_limit_max_hhmmss?: string | null;
  runtime_limit_event?: number | null;
  runtime_limit_terminate?: boolean;

  config: {
    fields: {
      name: string;
      Value: any;
      editable: boolean;
    }[];
  };
  created_at?: string;
  updated_at?: string;
  node_type_fields?: NodeTypeField[];
  audit_trail?: object;
  object_id?: number;
  zone_name?: any;
  sub_type?: string | null;
}

export interface NodeTypeField {
  name: string;
  type: string;
  Default?: any; // Optional field for default values
  JSONPath?: string; // Optional JSONPath for fetching dropdown options
  ValuesApi?: string; // Optional API endpoint for fetching dropdown values
}

interface PaginationState {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

// The config determines which headers are used in the list page views
// Node definitions, node instances
interface CustomerNodeDefinitionConfig {
  header_key: string;
  header_type: string;
  label: string;
}

export interface WhereUsedFlowRef {
  node_definition_id: number;
  name: string;
  zone_name: string;
  flow_node_id: string;
}

export interface WhereUsedTriggerRef {
  trigger_id: number;
  name: string;
  zone_name: string;
  action: string;
  role: 'watched' | 'target';
}

export interface WhereUsedEventRef {
  event_id: number;
  name: string;
  zone_name: string;
  type: string;
  link_type: 'wait' | 'raise';
}

export interface WhereUsedIntegrationNodeRef {
  integration_node_id: number;
  name: string;
  integration_name: string;
  zone_name: string;
}

export interface WhereUsedResult {
  node_definition_id?: number;
  flows: WhereUsedFlowRef[];
  triggers: WhereUsedTriggerRef[];
  events: WhereUsedEventRef[];
  active_instances: { total: number; by_status: Record<string, number> };
  integration_nodes: WhereUsedIntegrationNodeRef[];
}

export interface DeleteConflictPayload {
  error: string;
  references?: WhereUsedResult;
  blocking_instances?: { node_instance_id: number; status: string }[];
}

export interface AffectedFlow {
  parent_definition_id: number;
  flow_node_id: string;
  flow_node_label: string | null;
  flow_name: string;
  zone_name: string;
}

interface CustomerNodeDefinitionState {
  nodes: CustomerNodeDefinition[]; // The actual data
  auditTrailByNodeId: Record<number, any[]>;
  pagination: PaginationState; // Pagination-related information
  status: string | null; // Loading or error state
  search: string; // Search term
  sortBy: string; // Field to sort by
  sortOrder: 'asc' | 'desc'; // Sorting direction
  error: string | null; // Error message
  config: CustomerNodeDefinitionConfig[];
  filtersConfig: FilterConfig[];
  whereUsedById: Record<number, WhereUsedResult>;
  loading: {
    fetchCustomerNodeDefinitions: boolean;
    fetchCustomerNodeDefinitionById: boolean;
    fetchCustomerNodeDefinitionByZoneAndName: boolean;
    createCustomerNodeDefinition: boolean;
    updateCustomerNodeDefinition: boolean;
    deleteCustomerNodeDefinition: boolean;
    fetchCustomerNodeDefinitionAuditTrail: boolean;
    fetchWhereUsed: boolean;
  };
}

const initialState: CustomerNodeDefinitionState = {
  nodes: [],
  auditTrailByNodeId: {},
  pagination: {
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 2000,
  },
  status: null,
  search: '',
  sortBy: 'node_definition_id', // Default sort field
  sortOrder: 'asc', // Default sort order
  error: null,
  config: [],
  filtersConfig: [],
  whereUsedById: {},
  loading: {
    fetchCustomerNodeDefinitions: false,
    fetchCustomerNodeDefinitionById: false,
    fetchCustomerNodeDefinitionByZoneAndName: false,
    createCustomerNodeDefinition: false,
    updateCustomerNodeDefinition: false,
    deleteCustomerNodeDefinition: false,
    fetchCustomerNodeDefinitionAuditTrail: false,
    fetchWhereUsed: false,
  },
};

export interface FetchParams {
  page: number;
  limit: number;
  type: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  sortColumn?: string;
  search?: string;
}

// Fetch customer node definitions with pagination, sorting, and search
export const fetchCustomerNodeDefinitions = createAsyncThunk('customerNodeDefinitions/fetchCustomerNodeDefinitions', async (params: Record<string, any>) => {
  const cleanParams = {
    ...params,
    sortColumn: params.sortColumn || 'created_at',
    sortOrder: params.sortOrder || 'ASC',
    page: params.page || 1,
    type: params.type === 'All' ? null : params.type,
  };

  const res = await api.get('/node_definitions', {
    params: flattenQuery(cleanParams),
  });

  return res.data;
});

export const fetchCustomerNodeDefinitionById = createAsyncThunk<
  CustomerNodeDefinition, // The type of the payload the thunk resolves to
  number, // The type of the argument the thunk accepts
  { rejectValue: string } // Optional type for rejected value
>('customerNodeDefinitions/fetchCustomerNodeDefinitionById', async (id, { rejectWithValue }) => {
  try {
    const response = await api.get(`/node_definitions/${id}`);
    return response.data;
  } catch (error) {
    return rejectWithValue('Failed to fetch customer node definition');
  }
});

export const fetchCustomerNodeDefinitionByZoneAndName = createAsyncThunk<
  CustomerNodeDefinition, // The type of the payload the thunk resolves to
  { zone_name: string; node_definition_name: string }, // The type of the arguments the thunk accepts
  { rejectValue: string } // Optional type for rejected value
>('customerNodeDefinitions/fetchCustomerNodeDefinitionByZoneAndName', async ({ zone_name, node_definition_name }, { rejectWithValue }) => {
  try {
    const response = await api.get(`/node_definition/${zone_name}/${node_definition_name}`);
    return response.data;
  } catch (error) {
    return rejectWithValue('Failed to fetch customer node definition by zone and name');
  }
});

// Create a new customer node definition (send folder path as folder_name; do not send folder_id)
export const createCustomerNodeDefinition = createAsyncThunk(
  'customerNodeDefinitions/createCustomerNodeDefinition',
  async (definition: Omit<CustomerNodeDefinition | NewNode, 'node_definition_id'>, { rejectWithValue }) => {
    try {
      const def = definition as any;
      const { folder_id, ...body } = def;
      const path = def.folder ?? def.folder_name ?? '';
      if (path) body.folder_name = path;
      const response = await api.post('/node_definitions', body);
      return response.data;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, 'Failed to create node definition'));
    }
  }
);

// Update an existing customer node definition (send folder path as folder_name; do not send folder_id)
export const updateCustomerNodeDefinition = createAsyncThunk(
  'customerNodeDefinitions/updateCustomerNodeDefinition',
  async (
    payload: {
      definition: CustomerNodeDefinition | UpdatedNode;
      originalZoneName?: string;
      originalName?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const { definition, originalZoneName, originalName } = payload;
      const def = definition as any;

      const urlZoneName = originalZoneName || def.zone_name;
      const urlNodeName = originalName || def.name;

      const { folder_id, ...rest } = def;
      const path = def.folder ?? def.folder_name ?? '';
      const body = {
        ...rest,
        if_match_version: def.version,
      };
      if (path) body.folder_name = path;

      const response = await api.put(`/node_definitions/${urlZoneName}/${urlNodeName}`, body);
      return response.data;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, 'Failed to update node definition'));
    }
  }
);

// Delete a single customer node definition by ID
export const deleteCustomerNodeDefinition = createAsyncThunk<
  {
    node_definition_id: number;
    zone_name: string;
    node_definition_name: string;
    message: string;
  },
  { zone_name: string; node_definition_name: string },
  { rejectValue: DeleteConflictPayload }
>('customerNodeDefinitions/deleteCustomerNodeDefinition', async ({ zone_name, node_definition_name }, { rejectWithValue }) => {
  try {
    const response = await api.delete(`/node_definitions/${zone_name}/${node_definition_name}`);
    return {
      node_definition_id: response.data.node_definition_id,
      zone_name: response.data.zone_name,
      node_definition_name: response.data.node_definition_name,
      message: response.data?.message,
    };
  } catch (error: any) {
    const data = error.response?.data;
    return rejectWithValue({
      error: data?.error || 'Delete failed',
      references: data?.references,
      blocking_instances: data?.blocking_instances,
    });
  }
});

// Fetch all references to a node definition
export const fetchWhereUsed = createAsyncThunk<WhereUsedResult, number, { rejectValue: string }>(
  'customerNodeDefinitions/fetchWhereUsed',
  async (node_definition_id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/node_definitions/${node_definition_id}/where-used`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to load references');
    }
  }
);

// Delete multiple customer node definitions by IDs
// export const deleteCustomerNodeDefinitions = createAsyncThunk(
//   'customerNodeDefinitions/deleteCustomerNodeDefinitions',
//   async (customerNodeDefinitionIds: number[]) => {
//     await api.delete('/node_definitions', {
//       data: { ids: customerNodeDefinitionIds },
//     });
//     return customerNodeDefinitionIds;
//   }
// );

export const fetchCustomerNodeDefinitionAuditTrail = createAsyncThunk<CustomerNodeDefinition, { zone_name: string; node_definition_name: string }>(
  'customerNodeDefinitions/fetchCustomerNodeDefinitionAuditTrail',
  async ({ zone_name, node_definition_name }) => {
    try {
      const response = await api.get(`/node_definitions/${zone_name}/${node_definition_name}/audit_trail`);
      return response.data;
    } catch (error) {
      return error;
    }
  }
);

export const checkNodeDefinitionParameterUsage = createAsyncThunk<
  { affected_flows: AffectedFlow[] },
  { node_definition_id: number; parameter_names: string[] },
  { rejectValue: string }
>('customerNodeDefinitions/checkNodeDefinitionParameterUsage', async ({ node_definition_id, parameter_names }, { rejectWithValue }) => {
  try {
    const response = await api.get(`/node_definitions/${node_definition_id}/parameter-usages`, { params: { parameter_names: parameter_names.join(',') } });
    return response.data;
  } catch (error) {
    return rejectWithValue('Failed to check parameter usages');
  }
});

// export const deleteMultipleNodeDefinitions = createAsyncThunk<
//   { zone_name: string; node_definition_name: string }[], // return type
//   { zone_name: string; node_definition_name: string }[] // argument type
// >(
//   'customerNodeDefinitions/deleteMultipleNodeDefinitions',
//   async (identifiers, { rejectWithValue }) => {
//     try {
//       await api.delete('/node_definitions', {
//         headers: {
//           'Content-Type': 'application/json',
//         },
//         data: {
//           nodes: identifiers, // ✅ wrap in "nodes"
//         },
//       });
//       return identifiers;
//     } catch (error) {
//       return rejectWithValue(error.response.data.error);
//     }
//   }
// );

const customerNodeDefinitionsSlice = createSlice({
  name: 'customerNodeDefinitions',
  initialState,
  reducers: {
    setSortOrder: (state, action) => {
      state.sortBy = action.payload.sortBy;
      state.sortOrder = action.payload.sortOrder;
    },
    setSearch: (state, action) => {
      state.search = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomerNodeDefinitions.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.fetchCustomerNodeDefinitions = true;
      })
      .addCase(fetchCustomerNodeDefinitions.fulfilled, (state, action) => {
        const { pagination, nodes, config, filtersConfig } = action.payload;
        state.nodes = (nodes || []).map((node: any) => ({
          ...node,
          folder: node.folder ?? node.folder_name ?? '',
        }));
        state.config = config;
        state.filtersConfig = filtersConfig || [];
        state.pagination.totalItems = pagination.total_records;
        state.pagination.totalPages = pagination.total_pages;
        state.pagination.pageSize = pagination.limit;

        if (pagination.current_page > pagination.total_pages) {
          state.pagination.currentPage = 1;
        } else {
          state.pagination.currentPage = pagination.current_page;
        }
        state.loading.fetchCustomerNodeDefinitions = false;
      })
      .addCase(fetchCustomerNodeDefinitions.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'An error occurred';
        state.loading.fetchCustomerNodeDefinitions = false;
      })
      .addCase(fetchCustomerNodeDefinitionById.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.fetchCustomerNodeDefinitionById = true;
      })
      .addCase(fetchCustomerNodeDefinitionById.fulfilled, (state, action) => {
        const updatedItem = action.payload as any;
        updatedItem.folder = updatedItem.folder ?? updatedItem.folder_name ?? '';
        if (updatedItem.node_type_fields) {
          const combinedFields = updatedItem.node_type_fields.map((field) => ({
            ...field,
            ...updatedItem.config.fields.find((f) => f.name === field.name), // Merge matching config fields
          }));

          updatedItem.config.fields = combinedFields;
        }
        const index = state.nodes.findIndex((definition) => definition.node_definition_id === updatedItem.node_definition_id);
        // Only update existing nodes in the list, don't add new ones
        if (index !== -1) {
          state.nodes[index] = {
            ...state.nodes[index], // Keep existing data
            ...updatedItem, // Override with fetched data
          };
        } else {
          state.nodes.push(updatedItem);
        }
        state.status = null;
        state.loading.fetchCustomerNodeDefinitionById = false;
      })
      .addCase(fetchCustomerNodeDefinitionById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch customer node definition';
        state.loading.fetchCustomerNodeDefinitionById = false;
      })
      .addCase(createCustomerNodeDefinition.pending, (state) => {
        state.loading.createCustomerNodeDefinition = true;
      })
      .addCase(createCustomerNodeDefinition.fulfilled, (state, action) => {
        state.nodes.push(action.payload);
        state.loading.createCustomerNodeDefinition = false;
      })
      .addCase(createCustomerNodeDefinition.rejected, (state) => {
        state.loading.createCustomerNodeDefinition = false;
      })
      // Deep merge fields during an update
      .addCase(updateCustomerNodeDefinition.pending, (state) => {
        state.loading.updateCustomerNodeDefinition = true;
      })
      .addCase(updateCustomerNodeDefinition.fulfilled, (state, action) => {
        const incoming = action.payload as CustomerNodeDefinition;
        const index = state.nodes.findIndex((d) => d.node_definition_id === incoming.node_definition_id);
        if (index === -1) {
          state.loading.updateCustomerNodeDefinition = false;
          return;
        }

        // Start from the current item and shallow-merge the payload
        const current = state.nodes[index];

        // Always keep the whole payload (this includes retention_policies)
        state.nodes[index] = { ...current, ...incoming };

        // Preserve your existing behavior for fields merging (if server sends them)
        if (incoming?.config?.fields) {
          state.nodes[index].config = {
            ...(state.nodes[index].config || {}),
            fields: incoming.config.fields,
          };
        }
        state.loading.updateCustomerNodeDefinition = false;
      })
      .addCase(updateCustomerNodeDefinition.rejected, (state) => {
        state.loading.updateCustomerNodeDefinition = false;
      })

      .addCase(fetchCustomerNodeDefinitionAuditTrail.pending, (state) => {
        state.loading.fetchCustomerNodeDefinitionAuditTrail = true;
      })
      .addCase(fetchCustomerNodeDefinitionAuditTrail.fulfilled, (state, action) => {
        const objectId = Number(action.payload.object_id);
        if (Number.isFinite(objectId) && action?.payload?.audit_trail) {
          state.auditTrailByNodeId[objectId] = action.payload.audit_trail as any[];
        }
        state.loading.fetchCustomerNodeDefinitionAuditTrail = false;
      })

      .addCase(fetchCustomerNodeDefinitionAuditTrail.rejected, (state) => {
        state.loading.fetchCustomerNodeDefinitionAuditTrail = false;
      })

      .addCase(deleteCustomerNodeDefinition.pending, (state) => {
        state.loading.deleteCustomerNodeDefinition = true;
      })

      .addCase(deleteCustomerNodeDefinition.fulfilled, (state, action) => {
        const { node_definition_id, zone_name, node_definition_name, message } = action.payload;

        state.nodes = state.nodes.filter((n) => n.node_definition_id !== node_definition_id);

        toast.success(message || `Node definition '${node_definition_name}' in zone '${zone_name}' deleted.`);
        state.loading.deleteCustomerNodeDefinition = false;
      })

      .addCase(deleteCustomerNodeDefinition.rejected, (state, action) => {
        const message = action.payload?.error || 'Failed to delete node definition';
        state.error = message;

        // Only toast if there's no structured references payload — the caller
        // will render those in the confirmation modal instead.
        if (!action.payload?.references) {
          const toastId = 'delete-node-definition-failed';
          if (!toast.isActive(toastId)) {
            toast.error(`Failed to delete: ${message}`, { toastId });
          }
        }
        state.loading.deleteCustomerNodeDefinition = false;
      })

      .addCase(fetchWhereUsed.pending, (state) => {
        state.loading.fetchWhereUsed = true;
      })
      .addCase(fetchWhereUsed.fulfilled, (state, action) => {
        const id = action.payload.node_definition_id ?? action.meta.arg;
        state.whereUsedById[id] = action.payload;
        state.loading.fetchWhereUsed = false;
      })
      .addCase(fetchWhereUsed.rejected, (state) => {
        state.loading.fetchWhereUsed = false;
      })

      // .addCase(deleteCustomerNodeDefinitions.fulfilled, (state, action) => {
      //   state.nodes = state.nodes.filter(
      //     (definition) =>
      //       !action.payload.includes(definition.node_definition_id)
      //   );
      // })

      .addCase(fetchCustomerNodeDefinitionByZoneAndName.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.fetchCustomerNodeDefinitionByZoneAndName = true;
      })

      .addCase(fetchCustomerNodeDefinitionByZoneAndName.fulfilled, (state, action) => {
        const updatedItem = action.payload as CustomerNodeDefinition;
        (updatedItem as any).folder = (updatedItem as any).folder ?? (updatedItem as any).folder_name ?? '';

        // Merge node_type_fields with config.fields so UI can render combined values
        const cfgFields = updatedItem?.config?.fields ?? [];
        if (Array.isArray(updatedItem.node_type_fields)) {
          const combinedFields = updatedItem.node_type_fields.map((field: any) => ({
            ...field,
            ...(cfgFields.find((f: any) => f.name === field.name) || {}),
          }));

          updatedItem.config = {
            ...(updatedItem.config || {}),
            fields: combinedFields,
          };
        } else if (!updatedItem?.config) {
          // Ensure config exists for consumers expecting it
          updatedItem.config = { fields: cfgFields };
        }

        const index = state.nodes.findIndex((definition) => definition.node_definition_id === updatedItem.node_definition_id);

        if (index !== -1) {
          state.nodes[index] = { ...state.nodes[index], ...updatedItem };
        } else {
          state.nodes.push(updatedItem);
        }

        state.status = null; // Clear loading status
        state.loading.fetchCustomerNodeDefinitionByZoneAndName = false;
      })
      .addCase(fetchCustomerNodeDefinitionByZoneAndName.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch customer node definition by zone and name';
        state.loading.fetchCustomerNodeDefinitionByZoneAndName = false;
      });
    // .addCase(deleteMultipleNodeDefinitions.pending, (state) => {
    //   state.status = 'loading';
    //   state.error = null;
    // })
    // .addCase(deleteMultipleNodeDefinitions.fulfilled, (state, action) => {
    //   state.status = null;
    //   const deletedSet = new Set(
    //     action.payload.map((item) => `${item.zone_name}::${item.node_definition_name}`)
    //   );

    //   state.nodes = state.nodes.filter(
    //     (definition) =>
    //       !deletedSet.has(`${definition.zone_name}::${definition.name}`)
    //   );
    //   const toastId = 'data-succedeed-to-delete-node-definitions';
    //   if (!toast.isActive(toastId)) {
    //     toast.success(`Successfully deleted selected node definition/s!`, {
    //       toastId,
    //     });
    //   }
    // })
    // .addCase(deleteMultipleNodeDefinitions.rejected, (state, action) => {
    //   state.status = 'failed';

    //   const toastId = 'data-failed-to-delete-node-definitions';
    //   if (!toast.isActive(toastId)) {
    //     toast.error(
    //       `Failed to delete selected node definition/s! ${action.payload}`,
    //       { toastId }
    //     );
    //   }
    // });
  },
});

export const { setSortOrder, setSearch } = customerNodeDefinitionsSlice.actions;

export default customerNodeDefinitionsSlice.reducer;
